import { message } from 'antd';

class NotificationManager {
  constructor() {
    this.activeSyncs = new Map();
    this.currentLoading = null;
    this.finalMessageShown = false;
    this.lastProgressState = null;
    this.windowFocused = true;
    this.pendingUpdate = false;
    this.syncStartTime = null;
    this.timerInterval = null;
    this.initialSyncCount = 0;
    this.completedCount = 0;
    this.isOnlineCallback = null;

    // Store bound handlers for proper cleanup
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    // Track window focus to prevent notification stacking when app is not focused
    this.setupFocusHandlers();
  }

  setupFocusHandlers() {
    // Track window focus state
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('blur', this.handleBlur);

    // Also handle visibility API for better mobile support
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleFocus() {
    this.windowFocused = true;
    // Only cleanup if there are no active syncs, otherwise just update
    if (this.activeSyncs.size === 0) {
      this.forceCleanup();
    }
    // Process any pending updates when window regains focus
    if (this.pendingUpdate) {
      this.pendingUpdate = false;
      this.updateGlobalStatus();
    }
  }

  handleBlur() {
    this.windowFocused = false;
  }

  handleVisibilityChange() {
    if (!document.hidden && this.pendingUpdate) {
      this.windowFocused = true;
      // Only cleanup if there are no active syncs
      if (this.activeSyncs.size === 0) {
        this.forceCleanup();
      }
      this.pendingUpdate = false;
      this.updateGlobalStatus();
    } else if (document.hidden) {
      this.windowFocused = false;
    }
  }

  // MEMORY LEAK FIX: Add cleanup method for event listeners
  destroy() {
    window.removeEventListener('focus', this.handleFocus);
    window.removeEventListener('blur', this.handleBlur);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );

    // Clean up timer and notifications
    this.clearTimer();
    this.forceCleanup();

    // Clear internal state
    this.activeSyncs.clear();
    this.currentLoading = null;
    this.finalMessageShown = false;
    this.lastProgressState = null;
    this.pendingUpdate = false;
    this.syncStartTime = null;
    this.initialSyncCount = 0;
    this.completedCount = 0;
    this.isOnlineCallback = null;
  }

  // Set callback to check online state
  setOnlineStateCallback(callback) {
    this.isOnlineCallback = callback;
  }

  // Check if app is online
  isOnline() {
    if (this.isOnlineCallback) {
      return this.isOnlineCallback();
    }
    // Fallback to navigator.onLine if no callback provided
    return navigator.onLine;
  }

  // Force hide all notifications (called when going offline)
  hideAllNotifications() {
    this.forceCleanup();
    this.activeSyncs.clear();
    this.checkForFinalCleanup();
  }

  startSync(syncType, label, options = {}) {
    // Don't show sync notifications if app is offline
    if (!this.isOnline()) {
      return;
    }

    // If this is the first sync of a new batch, reset everything
    if (this.activeSyncs.size === 0) {
      this.completedCount = 0;
      this.initialSyncCount = 0;
      this.syncStartTime = null;
      this.finalMessageShown = false;
    }

    this.activeSyncs.set(syncType, {
      label,
      status: 'loading',
      startTime: Date.now(),
      warning: options.warning || null,
    });

    // Start global timer on first sync and set initial count
    if (!this.syncStartTime) {
      this.syncStartTime = Date.now();
      this.initialSyncCount = this.activeSyncs.size;
      this.startTimer();
    } else {
      // Update initial count if more syncs are added
      this.initialSyncCount = Math.max(
        this.initialSyncCount,
        this.activeSyncs.size,
      );
    }

    this.updateGlobalStatus();
  }

  startTimer() {
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.syncStartTime && this.activeSyncs.size > 0) {
        if (Date.now() - this.syncStartTime > 3 * 60 * 1000) {
          this.forceCleanup();
          this.activeSyncs.clear();
          this.checkForFinalCleanup();
          return;
        }
        this.updateGlobalStatus();
      }
    }, 1000);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  formatElapsedTime(startTime) {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  completeSync(syncType, success = true, errorMessage = null) {
    // Don't process sync completion if app is offline
    if (!this.isOnline()) {
      return;
    }

    const sync = this.activeSyncs.get(syncType);
    if (sync) {
      // Only increment if this sync hasn't been completed before
      if (sync.status === 'loading') {
        this.completedCount++;
      }

      sync.status = success ? 'success' : 'error';
      sync.errorMessage = errorMessage;
      sync.completedTime = Date.now();
      this.updateGlobalStatus();

      // Clean up completed syncs after a short delay
      setTimeout(() => {
        this.activeSyncs.delete(syncType);
        this.checkForFinalCleanup();
      }, 1000);
    }
  }

  updateGlobalStatus() {
    // Don't show notifications if app is offline
    if (!this.isOnline()) {
      return;
    }

    // If window is not focused, mark that we have a pending update and return
    if (!this.windowFocused) {
      this.pendingUpdate = true;
      return;
    }

    const syncs = Array.from(this.activeSyncs.values());
    const loading = syncs.filter((s) => s.status === 'loading');
    const completed = syncs.filter((s) => s.status === 'success');
    const failed = syncs.filter((s) => s.status === 'error');

    if (loading.length > 0) {
      // Still have syncs running - show/update progress with timer
      const total = this.initialSyncCount;
      const done = this.completedCount;
      const elapsedTime = this.syncStartTime
        ? this.formatElapsedTime(this.syncStartTime)
        : '';

      const currentState = `${Math.min(done, total)}/${total}`;
      const fullState = `${currentState}-${elapsedTime}`;

      // Check if any loading sync has a warning
      const currentWarning = loading.find((s) => s.warning)?.warning;

      // Clean, simple progress message
      let loadingMessage = `Sinhronizacija podataka (${currentState}) • ${elapsedTime}`;
      if (currentWarning) {
        loadingMessage += ` • ${currentWarning}`;
      }

      // Only create new message if we don't have one, otherwise reuse existing
      if (!this.currentLoading) {
        this.currentLoading = message.loading({
          content: loadingMessage,
          duration: 0,
          key: 'sync-progress', // Use key to update existing message
          style: {
            fontSize: '14px',
            opacity: '0.85',
            pointerEvents: 'none',
            userSelect: 'none',
          },
          className: 'click-through-notification',
        });
      } else {
        // Update existing message content
        message.loading({
          content: loadingMessage,
          duration: 0,
          key: 'sync-progress', // Same key will update the existing message
          style: {
            fontSize: '14px',
            opacity: '0.85',
            pointerEvents: 'none',
            userSelect: 'none',
          },
          className: 'click-through-notification',
        });
      }
      this.lastProgressState = fullState;
    } else if (
      loading.length === 0 &&
      (completed.length > 0 || failed.length > 0) &&
      !this.finalMessageShown
    ) {
      // All syncs completed - transition smoothly to final message
      this.currentLoading = null;
      this.lastProgressState = null;
      this.finalMessageShown = true;

      // Clear timer and calculate total sync time
      this.clearTimer();
      const totalTime = this.syncStartTime
        ? this.formatElapsedTime(this.syncStartTime)
        : '';
      this.syncStartTime = null;

      if (failed.length === 0) {
        message.success({
          content: `Podaci uspješno sinhronizirani • ${totalTime}`,
          duration: 3,
          key: 'sync-progress', // Same key for smooth transition
          style: {
            fontSize: '14px',
            opacity: '0.85',
            pointerEvents: 'none',
          },
          className: 'click-through-notification',
        });
      } else if (failed.length === completed.length + failed.length) {
        // All failed
        message.error({
          content: `Greška prilikom sinhronizacije podataka • ${totalTime}`,
          duration: 5,
          key: 'sync-progress', // Same key for smooth transition
          style: {
            fontSize: '14px',
            opacity: '0.85',
            pointerEvents: 'none',
          },
          className: 'click-through-notification',
        });
      } else {
        // Mixed results
        message.warning({
          content: `Sinhronizacija završena - ${failed.length} greški od ${
            completed.length + failed.length
          } operacija • ${totalTime}`,
          duration: 4,
          key: 'sync-progress', // Same key for smooth transition
          style: {
            fontSize: '14px',
            opacity: '0.85',
            pointerEvents: 'none',
          },
          className: 'click-through-notification',
        });
      }
    }
  }

  checkForFinalCleanup() {
    // Check if all syncs have been cleaned up
    if (this.activeSyncs.size === 0) {
      this.finalMessageShown = false;
      this.lastProgressState = null;
      this.clearTimer();
      this.syncStartTime = null;
      this.initialSyncCount = 0;
      this.completedCount = 0;
    }
  }

  forceCleanup() {
    // Force cleanup of any stale notifications when window regains focus
    if (this.currentLoading) {
      try {
        this.currentLoading();
      } catch (e) {
        // Ignore errors if notification was already closed
      }
      this.currentLoading = null;
    }

    // Only clear sync-related notifications, not all notifications
    message.destroy('sync-progress');
  }

  showError(text, duration = 3) {
    message.error(text, duration);
  }

  showSuccess(text, duration = 2) {
    message.success(text, duration);
  }

  showWarning(text, duration = 4) {
    message.warning(text, duration);
  }

  showInfo(text, duration = 3) {
    message.info(text, duration);
  }
}

export const notificationManager = new NotificationManager();
